import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSalveazaTargetareCampanie } from "@/hooks/api/mutate/useSalveazaTargetareCampanie";
import { useToast } from "@/hooks/use-toast";
import type {
  TargetareCampanie,
  TargetareCanalCampanie,
  TargetareStareCampanie,
} from "@/types/targetare";
import {
  ETICHETE_CANAL,
  ETICHETE_STARE_CAMPANIE,
} from "@/utils/targetareTinte";

type CampanieDialogProps = {
  deschis: boolean;
  onClose: () => void;
  campanie: TargetareCampanie | null;
};

export function CampanieDialog({
  deschis,
  onClose,
  campanie,
}: CampanieDialogProps) {
  const { toast } = useToast();
  const salveaza = useSalveazaTargetareCampanie();
  const [nume, setNume] = useState("");
  const [canal, setCanal] = useState<TargetareCanalCampanie>("email");
  const [sector, setSector] = useState("");
  const [descriere, setDescriere] = useState("");
  const [stare, setStare] = useState<TargetareStareCampanie>("planificata");
  const [externId, setExternId] = useState("");
  const [externUrl, setExternUrl] = useState("");

  useEffect(() => {
    if (!deschis) return;
    setNume(campanie?.nume ?? "");
    setCanal(campanie?.canal ?? "email");
    setSector(campanie?.sector ?? "");
    setDescriere(campanie?.descriere ?? "");
    setStare(campanie?.stare ?? "planificata");
    setExternId(campanie?.extern_id ?? "");
    setExternUrl(campanie?.extern_url ?? "");
  }, [deschis, campanie]);

  const trimite = () => {
    salveaza.mutate(
      {
        id: campanie?.id,
        nume: nume.trim(),
        canal,
        sector,
        descriere,
        stare,
        extern_id: externId,
        extern_url: externUrl,
      },
      {
        onSuccess: () => {
          toast({
            title: campanie ? "Campanie actualizată" : "Campanie creată",
          });
          onClose();
        },
        onError: (e) =>
          toast({
            title: "Salvarea a eșuat",
            description: e.message,
            variant: "destructive",
          }),
      }
    );
  };

  return (
    <Dialog open={deschis} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {campanie ? "Editează campania" : "Campanie nouă"}
          </DialogTitle>
          <DialogDescription>
            O campanie grupează țintele pe un canal (Mailchimp, Google Ads,
            LinkedIn, telefon). Id-ul și adresa externă leagă campania de
            instrumentul folosit, pentru statistici.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            value={nume}
            onChange={(e) => setNume(e.target.value)}
            placeholder="Nume campanie (ex. Energie · ofertă audit NIS2)"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              value={canal}
              onValueChange={(v) => setCanal(v as TargetareCanalCampanie)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Canal" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(ETICHETE_CANAL) as TargetareCanalCampanie[]).map(
                  (c) => (
                    <SelectItem key={c} value={c}>
                      {ETICHETE_CANAL[c]}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
            <Select
              value={stare}
              onValueChange={(v) => setStare(v as TargetareStareCampanie)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Stare" />
              </SelectTrigger>
              <SelectContent>
                {(
                  Object.keys(
                    ETICHETE_STARE_CAMPANIE
                  ) as TargetareStareCampanie[]
                ).map((s) => (
                  <SelectItem key={s} value={s}>
                    {ETICHETE_STARE_CAMPANIE[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            placeholder="Sector vizat (opțional)"
          />
          <Textarea
            value={descriere}
            onChange={(e) => setDescriere(e.target.value)}
            rows={3}
            placeholder="Descriere, ofertă, mesaj-cheie (opțional)"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              value={externId}
              onChange={(e) => setExternId(e.target.value)}
              placeholder="Id extern (audiență Mailchimp, campanie Ads)"
            />
            <Input
              value={externUrl}
              onChange={(e) => setExternUrl(e.target.value)}
              placeholder="Adresă externă (opțional)"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Renunță
          </Button>
          <Button
            onClick={trimite}
            disabled={!nume.trim() || salveaza.isPending}
          >
            {salveaza.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
